/**
 * Rest-pose alignment (`RigBinding.restAlignment`, rigVersion `humanoid-v2`).
 *
 * The canonical rest convention (D4) encodes a rig's rest pose entirely in bone *positions* —
 * every rest rotation is identity. That makes `q_c` a rotation away from **whatever rest the rig
 * happens to have**, so a clip authored on a T-pose rig arrives on an A-pose rig as an extra
 * rotation on top of an already-lowered arm: measured 237 mm at the elbow and ~470 mm at the hand
 * for a 45 deg shoulder difference.
 *
 * `E_b` closes that gap: the minimal swing taking the bone's direction in the canonical straight
 * T-pose (`REST_REFERENCE_POSITIONS`) to its direction in this rig's rest.
 *
 *     q_abs(b) = E(parent)^-1 * q_rel(b) * E(b)      what a canonical clip stores
 *     q_rel(b) = E(parent)   * q_abs(b) * E(b)^-1    what a rig is driven with
 *
 * The correction is **two-sided**. `q_c` is written into the bone's local slot
 * (`canonicalToSourceLocal`), so it is a parent-relative quantity wearing world axes; a one-sided
 * per-bone factor makes the child apply its parent's alignment a second time. All three one-sided
 * candidates were measured and none closes the chain (elbow 0 mm but hand 49-208 mm off), while
 * the two-sided form is exact to 0.0 mm.
 *
 * Pure tuple math apart from the direction geometry: no editor state, no DOM.
 */

import { Quaternion, Vector3 } from 'three';

import {
  CANONICAL_BONE_IDS,
  CANONICAL_PARENT,
  CANONICAL_PRIMARY_CHILD,
  REST_REFERENCE_POSITIONS,
  cloneQuat,
  identityQuat,
  normalizeQuat,
  quatInvert,
  quatMultiply,
} from '../shared';
import type { CanonicalBoneId, QuaternionTuple, Vec3Tuple } from '../shared';
import type { SourceBoneInfo } from './sourceSkeleton';

/** Bone directions shorter than this carry no usable orientation. */
const MIN_BONE_LENGTH = 1e-5;

/** Beyond this the swing is a near-180 deg flip about an arbitrary axis — refuse to guess. */
const MAX_ALIGNMENT_DOT = -0.999;

export interface RestAlignmentResult {
  alignment: Partial<Record<CanonicalBoneId, QuaternionTuple>>;
  /** Human-readable notes for the bones that could not be aligned. */
  warnings: string[];
}

/**
 * Rest world joint positions, already in canonical world space, for every bone a mapping
 * resolves. `worldCorrection` is applied here so callers can pass raw skeleton analysis.
 */
export function canonicalRestPositions(
  mapping: Partial<Record<CanonicalBoneId, string>>,
  bones: readonly SourceBoneInfo[],
  worldCorrection: QuaternionTuple = identityQuat(),
): Partial<Record<CanonicalBoneId, Vec3Tuple>> {
  const byName = new Map<string, SourceBoneInfo>();
  for (const b of bones) if (!byName.has(b.name)) byName.set(b.name, b);

  const correction = new Quaternion(
    worldCorrection[0],
    worldCorrection[1],
    worldCorrection[2],
    worldCorrection[3],
  );
  const scratch = new Vector3();

  const out: Partial<Record<CanonicalBoneId, Vec3Tuple>> = {};
  for (const id of CANONICAL_BONE_IDS) {
    const name = mapping[id];
    if (!name) continue;
    const info = byName.get(name);
    if (!info) continue;
    const p = info.restWorldPosition;
    scratch.set(p[0], p[1], p[2]).applyQuaternion(correction);
    out[id] = [scratch.x, scratch.y, scratch.z];
  }
  return out;
}

/**
 * `E_b` for every bone whose direction can be measured.
 *
 * A bone needs its primary child to have a direction at all, so leaves (`Root`, `Head`, hands,
 * feet) get **their parent's** alignment. Combined with the two-sided formula that makes their
 * correction cancel to identity — i.e. a hand keeps the rest-relative rotation it was authored
 * with, which is the conservative answer when a wrist's twist axis is unknowable.
 */
export function computeRestAlignment(
  restPositions: Partial<Record<CanonicalBoneId, Vec3Tuple>>,
): RestAlignmentResult {
  const warnings: string[] = [];
  const alignment: Partial<Record<CanonicalBoneId, QuaternionTuple>> = {};

  const canonical = new Vector3();
  const source = new Vector3();
  const swing = new Quaternion();

  for (const id of CANONICAL_BONE_IDS) {
    const child = CANONICAL_PRIMARY_CHILD[id];
    if (!child) continue;

    const from = restPositions[id];
    const to = restPositions[child];
    if (!from || !to) continue;

    canonical
      .set(...REST_REFERENCE_POSITIONS[child])
      .sub(new Vector3(...REST_REFERENCE_POSITIONS[id]));
    source.set(to[0] - from[0], to[1] - from[1], to[2] - from[2]);

    if (canonical.length() < MIN_BONE_LENGTH || source.length() < MIN_BONE_LENGTH) {
      warnings.push(
        `Bone "${id}" and "${child}" sit on top of each other in the rest pose, so the bone has ` +
          'no direction; it was left unaligned.',
      );
      continue;
    }

    canonical.normalize();
    source.normalize();

    if (canonical.dot(source) < MAX_ALIGNMENT_DOT) {
      warnings.push(
        `Bone "${id}" points the opposite way to the canonical rest pose; the rotation needed to ` +
          'align it is ambiguous, so it was left unaligned.',
      );
      continue;
    }

    swing.setFromUnitVectors(canonical, source);
    alignment[id] = normalizeQuat([swing.x, swing.y, swing.z, swing.w]);
  }

  // Leaves inherit the alignment of the nearest ancestor that has one.
  for (const id of CANONICAL_BONE_IDS) {
    if (CANONICAL_PRIMARY_CHILD[id]) continue;
    let cursor = CANONICAL_PARENT[id];
    while (cursor) {
      const inherited = alignment[cursor];
      if (inherited) {
        alignment[id] = cloneQuat(inherited);
        break;
      }
      cursor = CANONICAL_PARENT[cursor];
    }
  }

  return { alignment, warnings };
}

// ---------------------------------------------------------------------------
// The correction itself
// ---------------------------------------------------------------------------

/**
 * Per-bone factors for the two-sided correction, resolved against the mapping so `parent` is the
 * nearest bone that actually has an alignment. Precomputed once at bind time because `applyPose`
 * runs every frame and must not allocate.
 */
export interface BoneAlignment {
  /** `E(parent)` */
  parent: QuaternionTuple;
  /** `E(parent)^-1` */
  parentInverse: QuaternionTuple;
  /** `E(b)` */
  own: QuaternionTuple;
  /** `E(b)^-1` */
  ownInverse: QuaternionTuple;
  /** True when both factors are identity — the correction is a no-op and can be skipped. */
  identity: boolean;
}

/** Shared no-op factors. Never mutated, so every unaligned bone can point at this one object. */
export const IDENTITY_ALIGNMENT: BoneAlignment = {
  parent: identityQuat(),
  parentInverse: identityQuat(),
  own: identityQuat(),
  ownInverse: identityQuat(),
  identity: true,
};

function isIdentityQuat(q: QuaternionTuple): boolean {
  return Math.abs(Math.abs(q[3]) - 1) < 1e-6;
}

/**
 * Resolve `restAlignment` into per-bone factors for the bones a binding actually drives.
 * `resolvable(id)` reports whether a canonical bone is mapped, so the parent lookup can skip
 * unmapped ancestors instead of double-counting them.
 */
export function resolveBoneAlignments(
  restAlignment: Partial<Record<CanonicalBoneId, QuaternionTuple>> | undefined,
  isMapped: (id: CanonicalBoneId) => boolean,
): Map<CanonicalBoneId, BoneAlignment> {
  const out = new Map<CanonicalBoneId, BoneAlignment>();
  if (!restAlignment) return out;

  for (const id of CANONICAL_BONE_IDS) {
    if (!isMapped(id)) continue;

    const own = restAlignment[id] ?? identityQuat();

    let parentId = CANONICAL_PARENT[id];
    while (parentId && !isMapped(parentId)) parentId = CANONICAL_PARENT[parentId];
    const parent = (parentId ? restAlignment[parentId] : undefined) ?? identityQuat();

    if (isIdentityQuat(own) && isIdentityQuat(parent)) {
      out.set(id, IDENTITY_ALIGNMENT);
      continue;
    }

    out.set(id, {
      parent: cloneQuat(parent),
      parentInverse: quatInvert(parent),
      own: cloneQuat(own),
      ownInverse: quatInvert(own),
      identity: false,
    });
  }

  return out;
}

// Module-level scratch: these run once per bone per frame during playback.
const scratch: QuaternionTuple = [0, 0, 0, 1];

/** Rig-independent clip value → the rotation to drive this rig with. `E(p) * q_abs * E(b)^-1`. */
export function absoluteToRelative(
  a: BoneAlignment,
  qAbs: QuaternionTuple,
  out?: QuaternionTuple,
): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  if (a.identity) {
    target[0] = qAbs[0];
    target[1] = qAbs[1];
    target[2] = qAbs[2];
    target[3] = qAbs[3];
    return target;
  }
  quatMultiply(a.parent, qAbs, scratch);
  quatMultiply(scratch, a.ownInverse, target);
  return normalizeQuat(target, target);
}

/** The rotation currently driving this rig → the rig-independent clip value. */
export function relativeToAbsolute(
  a: BoneAlignment,
  qRel: QuaternionTuple,
  out?: QuaternionTuple,
): QuaternionTuple {
  const target = out ?? ([0, 0, 0, 1] as QuaternionTuple);
  if (a.identity) {
    target[0] = qRel[0];
    target[1] = qRel[1];
    target[2] = qRel[2];
    target[3] = qRel[3];
    return target;
  }
  quatMultiply(a.parentInverse, qRel, scratch);
  quatMultiply(scratch, a.own, target);
  return normalizeQuat(target, target);
}
